class ArticleShare extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.details = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.actions = this.querySelector('.article-share__actions');
    this.status = this.querySelector('[role="status"]');
    this.copyButton = this.querySelector('[data-copy-link]');
    this.expanded = this.details.open;
    this.actions.inert = !this.expanded;
    this.toggle.setAttribute('aria-expanded', String(this.expanded));

    this.toggle.addEventListener('click', (event) => {
      event.preventDefault();
      this.setExpanded(!this.expanded);
    }, { signal });

    this.copyButton.addEventListener('click', () => this.copyLink(), { signal });
    this.details.addEventListener('toggle', () => {
      if (!this.details.open) this.clearStatus();
    }, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.setExpanded(false);
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.expanded) return;
      this.setExpanded(false);
      if (this.contains(document.activeElement)) this.toggle.focus();
    }, { signal });
    window.addEventListener('resize', () => {
      if (this.animation) this.finishExpansion();
    }, { signal });
  }

  disconnectedCallback() {
    this.controller.abort();
    this.animation?.cancel();
    this.actionsAnimation?.cancel();
    clearTimeout(this.statusTimer);
  }

  setExpanded(expanded) {
    if (expanded === this.expanded) return;
    const start = this.details.getBoundingClientRect();
    const opacity = this.details.open ? getComputedStyle(this.actions).opacity : 0;
    this.animation?.cancel();
    this.actionsAnimation?.cancel();
    this.expanded = expanded;
    this.details.open = true;
    this.actions.inert = !expanded;
    this.toggle.setAttribute('aria-expanded', String(expanded));
    if (!expanded) this.clearStatus();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finishExpansion();
      return;
    }

    const end = (expanded ? this.details : this.toggle).getBoundingClientRect();
    const timing = { duration: 220, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
    this.animation = this.details.animate([
      { width: `${start.width}px`, height: `${start.height}px` },
      { width: `${end.width}px`, height: `${end.height}px` }
    ], timing);
    this.actionsAnimation = this.actions.animate([
      { opacity },
      { opacity: expanded ? 1 : 0 }
    ], timing);
    this.animation.onfinish = () => this.finishExpansion();
  }

  finishExpansion() {
    this.details.open = this.expanded;
    this.animation?.cancel();
    this.actionsAnimation?.cancel();
    this.animation = null;
    this.actionsAnimation = null;
  }

  clearStatus() {
    clearTimeout(this.statusTimer);
    this.status.textContent = '';
  }

  async copyLink() {
    this.clearStatus();
    this.copyButton.disabled = true;
    try {
      await navigator.clipboard.writeText(this.dataset.url);
      this.status.textContent = this.dataset.copied;
      this.statusTimer = setTimeout(() => this.clearStatus(), 4000);
    } catch {
      this.status.textContent = this.dataset.copyError;
    } finally {
      this.copyButton.disabled = false;
    }
  }
}

if (!customElements.get('article-share')) {
  customElements.define('article-share', ArticleShare);
}
